import axios, { AxiosInstance } from 'axios'
import toast from 'react-hot-toast'

/**
 * Unified Authentication API Client for Admin Panel v2
 * 
 * This uses the same magic link authentication system as the main frontend,
 * but validates that the user has admin privileges (user_type = "admin").
 * 
 * Benefits:
 * - Consistent authentication across all applications
 * - No separate admin user management
 * - Leverages existing magic link infrastructure
 * - Automatic security features (rate limiting, token blacklisting, etc.)
 */

export interface AdminLoginRequest {
  email: string
}

export interface AdminUser {
  id: string
  email: string
  user_type: string
  first_name?: string
  last_name?: string
  company?: string
  is_active: boolean
  is_verified: boolean
  created_at: string
}

export interface AuthTokens {
  access_token: string
  refresh_token?: string
  token_type: string
  expires_in: number
}

export interface AuthResponse {
  success: boolean
  message?: string
  error?: string
  user?: AdminUser
}

class UnifiedAuthApiClient {
  private client: AxiosInstance
  private accessToken: string | null = null

  constructor() {
    this.client = axios.create({
      baseURL: '/api', // Use the unified API base for both auth and admin endpoints
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Load token from storage
    this.loadTokenFromStorage()

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.clearAuth()
          window.location.href = '/login'
        } else if (error.response?.status >= 500) {
          toast.error('Server error occurred. Please try again.')
        } else if (error.response?.status === 403) {
          toast.error('Admin access required. Please contact support.')
        }
        return Promise.reject(error)
      }
    )
  }

  private loadTokenFromStorage() {
    try {
      const token = localStorage.getItem('admin_auth_token')
      if (token) {
        this.accessToken = token
      }
    } catch (error) {
      console.error('Failed to load auth token:', error)
    }
  }

  private saveTokenToStorage(token: string) {
    try {
      localStorage.setItem('admin_auth_token', token)
      this.accessToken = token
    } catch (error) {
      console.error('Failed to save auth token:', error)
    }
  }

  private clearAuth() {
    this.accessToken = null
    localStorage.removeItem('admin_auth_token')
  }

  /**
   * Step 1: Request magic link for admin login (admin-specific endpoint)
   */
  async requestMagicLink(email: string): Promise<AuthResponse> {
    try {
      // Try admin-specific endpoint first, fallback to regular endpoint
      let response
      try {
        response = await this.client.post('/admin/request-magic-link', { email })
      } catch (adminError) {
        // Fallback to regular magic link endpoint
        response = await this.client.post('/auth/magic-link/login', { email })
      }
      
      return {
        success: true,
        message: response.data.message || 'Admin magic link sent! Check console for link.'
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to send magic link'
      return {
        success: false,
        error: errorMessage
      }
    }
  }

  /**
   * Step 2: Verify magic link token and validate admin access
   */
  async verifyMagicLink(token: string): Promise<AuthResponse> {
    try {
      console.log('🔍 UnifiedAuthApi: Starting verifyMagicLink', {
        tokenPreview: token.substring(0, 10) + '...',
        endpoint: '/auth/verify'
      })
      
      const response = await this.client.post('/auth/verify', { token })
      console.log('🔍 UnifiedAuthApi: Verify response received', {
        status: response.status,
        hasData: !!response.data,
        hasAccessToken: !!response.data?.access_token
      })
      
      const data: AuthTokens = response.data

      // Save the access token
      console.log('🔍 UnifiedAuthApi: Saving access token to storage')
      this.saveTokenToStorage(data.access_token)

      // Get user data to verify admin access
      console.log('🔍 UnifiedAuthApi: Getting current user data...')
      const userResponse = await this.getCurrentUser()
      
      console.log('🔍 UnifiedAuthApi: getCurrentUser response:', {
        success: userResponse.success,
        hasUser: !!userResponse.user,
        error: userResponse.error,
        userType: userResponse.user?.user_type
      })
      
      if (!userResponse.success || !userResponse.user) {
        console.error('❌ UnifiedAuthApi: Failed to get user information')
        this.clearAuth()
        return {
          success: false,
          error: 'Failed to get user information'
        }
      }

      // Validate admin access
      if (userResponse.user.user_type !== 'admin') {
        console.error('❌ UnifiedAuthApi: User is not admin', {
          userType: userResponse.user.user_type,
          email: userResponse.user.email
        })
        this.clearAuth()
        return {
          success: false,
          error: 'Admin access required. This account does not have administrative privileges.'
        }
      }

      console.log('✅ UnifiedAuthApi: Admin validation successful', {
        userEmail: userResponse.user.email,
        userType: userResponse.user.user_type
      })

      return {
        success: true,
        user: userResponse.user,
        message: 'Admin login successful'
      }

    } catch (error: any) {
      console.error('❌ UnifiedAuthApi: Exception during verification:', {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      })
      this.clearAuth()
      const errorMessage = error.response?.data?.detail || 'Magic link verification failed'
      return {
        success: false,
        error: errorMessage
      }
    }
  }

  /**
   * Get current admin user information
   */
  async getCurrentUser(): Promise<AuthResponse> {
    if (!this.accessToken) {
      return {
        success: false,
        error: 'No access token available'
      }
    }

    try {
      const response = await this.client.get('/auth/me')
      const user: AdminUser = response.data

      // Validate admin access
      if (user.user_type !== 'admin') {
        this.clearAuth()
        return {
          success: false,
          error: 'Admin access required'
        }
      }

      return {
        success: true,
        user
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to get user data'
      return {
        success: false,
        error: errorMessage
      }
    }
  }

  /**
   * Logout admin user
   */
  async logout(): Promise<void> {
    try {
      if (this.accessToken) {
        await this.client.post('/auth/logout')
      }
    } catch (error) {
      console.error('Logout request failed:', error)
    } finally {
      this.clearAuth()
    }
  }

  /**
   * Check if user is authenticated and has admin access
   */
  async isAuthenticated(): Promise<boolean> {
    if (!this.accessToken) {
      return false
    }

    const userResponse = await this.getCurrentUser()
    return userResponse.success && userResponse.user?.user_type === 'admin'
  }

  /**
   * Get access token for API requests
   */
  getAccessToken(): string | null {
    return this.accessToken
  }
}

export const unifiedAuthApi = new UnifiedAuthApiClient()